import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { ChapterSummary } from "@domain/lms/course/courseContent";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ChevronDownIcon } from "@ui/components/icons";
import { gap } from "@ui/styles/utils";

interface FoldableChapterProgressProps extends React.HTMLAttributes<HTMLDivElement> {
	courseId: string;
	chapterIndex: number;
	chapter: ChapterSummary;
	isCurrent: boolean;
}
export const FoldableChapterProgress: React.FC<FoldableChapterProgressProps> = ({
	courseId,
	chapterIndex,
	chapter,
	isCurrent,
	...htmlProps
}) => {
	const hasSubSections = chapter.sections.length > 0;
	const [opened, setOpened] = useState(isCurrent && hasSubSections);

	useEffect(() => {
		setOpened(isCurrent && hasSubSections);
	}, [isCurrent]);

	return (
		<Container {...htmlProps}>
			<ChapterTitle
				onClick={() => {
					if (hasSubSections) {
						setOpened(!opened);
					}
				}}
				openable={hasSubSections}
				opened={opened}
				isCurrent={isCurrent}
			>
				<Link to={Path.Lms.CourseContent(courseId, chapterIndex)}>
					<Primary>
						<b>
							{chapterIndex + 1}. {chapter.title}
						</b>
					</Primary>
				</Link>
				{hasSubSections && <ChevronDownIcon />}
			</ChapterTitle>
			{opened && (
				<SectionsSummary isCurrent={isCurrent}>
					{chapter.sections.map((section) => (
						<Link key={section.id} to={Path.Lms.CourseContent(courseId, chapterIndex, section.id)}>
							<Primary>{section.title}</Primary>
						</Link>
					))}
				</SectionsSummary>
			)}
		</Container>
	);
};

const Container = styled.div``;

const ChapterTitle = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => !["opened", "openable", "isCurrent"].includes(prop),
})<{ opened: boolean; openable: boolean; isCurrent: boolean }>`
	cursor: pointer;
	${({ openable }) => !openable && "pointer-events: none"};
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	padding: 15px 20px;
	border-top: none;
	${({ opened }) => !opened && "border: none"};
	${gap(10, "row")};

	svg {
		flex-shrink: 0;
		${({ opened }) => opened && "transform: rotate(180deg)"};
	}

	a {
		pointer-events: all;
		div {
			${({ theme, isCurrent }) => isCurrent && `color: ${theme.colors.primary}`};
		}
		:hover div {
			color: ${({ theme }) => theme.colors.primary};
		}
	}
`;

const SectionsSummary = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isCurrent",
})<{ isCurrent: boolean }>`
	padding: 15px 20px;
	${gap(10)};

	a  {
		display: block;
		:hover div {
			color: ${({ theme }) => theme.colors.primary};
		}
		&:not(:last-child) {
			padding-bottom: 10px;
			border-bottom: 1px solid ${({ theme }) => theme.colors.lightgray};
		}
	}
`;
